import React from 'react';
import { Agent } from '../../types';
import s from './AppSelector.module.css';

export type AppChoice = 'saas' | 'support';

interface Props {
  agent: Agent;
  onSelect: (choice: AppChoice) => void;
  onSignOut: () => void;
}

/**
 * AppSelector
 * ─────────────────────────────────────────────────────────────────────────────
 * Post-login landing screen. Presents two consoles as cards — the SaaS Admin
 * console and the Support Console — and lets the signed-in agent choose which
 * one to open. Mirrors the LoginScreen visual language (grid + glow + card).
 */
export const AppSelector: React.FC<Props> = ({ agent, onSelect, onSignOut }) => {
  console.log("Agent:", agent);

  return (
    <div className={s.screen}>
      <div className={s.grid} />
      <div className={s.glow} />

      <div className={s.wrap}>
        <div className={s.brand}>
          <img src="../assets/logo.png" alt="Logipulse" />
          <div>
            <div className={s.titleText}>Logipulse</div>
            <div className={s.brandSub}>Choose a console</div>
          </div>
        </div>

        <h1 className={s.heading}>Welcome, {agent.role.split(' ')[0]}</h1>
        <p className={s.sub}>Select the console you'd like to open</p>

        <div className={s.cards}>
          <div
            className={s.card}
            role="button"
            tabIndex={0}
            onClick={() => onSelect('saas')}
            onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onSelect('saas')}
          >
            <div className={`${s.cardIcon} ${s.iconAdmin}`}>🛡</div>
            <div className={s.cardTitle}>SaaS Admin</div>
            <div className={s.cardDesc}>
              Tenants, usage tracking, payments, invoices, reminders and the
              UI-feature tree — the full platform administration console.
            </div>
            <span className={s.cardCta}>Open SaaS Admin →</span>
          </div>

          <div
            className={s.card}
            role="button"
            tabIndex={0}
            onClick={() => onSelect('support')}
            onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onSelect('support')}
          >
            <div className={`${s.cardIcon} ${s.iconSupport}`}>🎧</div>
            <div className={s.cardTitle}>Support Console</div>
            <div className={s.cardDesc}>
              Live user activity, guidance flows, UI-tree highlighting and
              real-time chat — everything your support agents need.
            </div>
            <span className={s.cardCta}>Open Support Console →</span>
          </div>
        </div>

        <div className={s.footer}>
          <button className={s.signOut} onClick={onSignOut}>Sign out</button>
        </div>
      </div>
    </div>
  );
};
